import { useMemo } from 'react'
import { useDetalhesFinos, useNivelQualidade } from '../ambiente/contextoQualidade'
import { CaixaArredondada } from '../componentes/CaixaArredondada'
import { malhasExtensor, type MalhasExtensor } from './malhasExtensor'

interface Props {
  /**
   * Progresso de extensão (0 = totalmente recolhido, 1 = extensão máxima nos 2 estágios).
   */
  progresso?: number
  /**
   * Curso direto em metros (caso fornecido pela cinemática).
   */
  curso?: number
  /**
   * Destaca o equipamento quando ativo.
   */
  ativa?: boolean
  /**
   * Modo do alargador ('abrir' = estendendo 2 estágios, 'fechar' = recolhendo).
   */
  modo?: 'abrir' | 'fechar'
}

const LARANJA_HOLMATRO = '#ea580c'
const LARANJA_BRILHO = '#f97316'
const ACO_CROMADO_ESTAGIO1 = '#cbd5e1'
const ACO_CROMADO_ESTAGIO2 = '#e2e8f0'
const ACO_GARRAS = '#94a3b8'
const GRAFITE_COLAR = '#1e2328'
const BORRACHA_PUNHO = '#111827'

/**
 * Garra de 4 dentes em cruz (cabeça e pé do extensor) para ancoragem estrutural firme.
 * Os dentes são pirâmides de base quadrada inclinadas para fora: mordem a chapa
 * pelas quinas, como as garras reais, onde as caixas arredondadas escorregavam.
 */
function GarraDentadaCruz({ malhas, inverter = false, detalhes }: { malhas: MalhasExtensor; inverter?: boolean; detalhes: boolean }) {
  return (
    <group rotation={[0, 0, inverter ? Math.PI : 0]}>
      <mesh geometry={malhas.garra} castShadow>
        <meshStandardMaterial color={ACO_GARRAS} metalness={0.85} roughness={0.28} />
      </mesh>

      {[0, Math.PI / 2, Math.PI, (3 * Math.PI) / 2].map((angulo) => (
        <group key={angulo} rotation={[angulo, 0, 0]}>
          <mesh position={[0.03, 0.026, 0]} rotation={[0, 0, -Math.PI / 2 + 0.35]}>
            <coneGeometry args={[0.013, 0.026, 4]} />
            <meshStandardMaterial color="#64748b" metalness={0.9} roughness={0.22} />
          </mesh>
        </group>
      ))}

      {detalhes && (
        <mesh position={[0.02, 0, 0]} rotation={[0, 0, Math.PI / 2]}>
          <cylinderGeometry args={[0.01, 0.01, 0.004, 12]} />
          <meshStandardMaterial color="#0f172a" roughness={0.6} />
        </mesh>
      )}
    </group>
  )
}

/**
 * Cilindro extensor telescópico de 2 estágios, inspirado no Holmatro.
 * Guarda a silhueta e as cores (laranja e preto), sem logo nem texto.
 *
 * - Corpo laranja torneado com anel traseiro e tubo de óleo curvo por cima;
 * - garras de 4 dentes em cruz na base e na cabeça;
 * - colar grafite com manopla de comando vertical estriada e anel de segurança;
 * - alça de transporte paralela ao corpo;
 * - dois estágios cromados com anel guia de vedação na boca de cada um.
 *
 * A extensão dos estágios é a mesma do modelo anterior (36 cm cada); só a
 * forma mudou. Os estágios esticam por escala de um tubo unitário, sem
 * recriar geometria a cada quadro.
 */
export function CilindroTelescopicoHolmatro3D({
  progresso = 0,
  curso,
  ativa = false,
  modo = 'abrir',
}: Props) {
  const nivel = useNivelQualidade()
  const detalhes = useDetalhesFinos()
  const malhas = malhasExtensor(nivel)

  // Converte curso ou progresso para a extensão proporcional dos 2 estágios
  const { ext1, ext2, posPonta } = useMemo(() => {
    const maxEstagio1 = 0.36 // 36 cm para cada estágio (total 72 cm de curso telescópico)
    const maxEstagio2 = 0.36

    let fator: number
    if (curso != null) {
      fator = Math.min(1, Math.max(0, (curso - 0.18) / 1.12))
    } else {
      fator = Math.min(1, Math.max(0, progresso))
    }

    if (modo === 'fechar') {
      fator = 1 - fator
    }

    // Extensão sincronizada dos dois estágios telescópicos
    const s1 = fator * maxEstagio1
    const s2 = fator * maxEstagio2
    const ponta = s1 + s2 + 0.04

    return { ext1: s1, ext2: s2, posPonta: ponta }
  }, [progresso, curso, modo])

  return (
    <group>
      {/* 1. Base traseira de apoio */}
      <group position={[-0.54, 0, 0]}>
        <GarraDentadaCruz malhas={malhas} inverter detalhes={detalhes} />
      </group>

      {/* 2. Corpo laranja, anel traseiro e tubo de óleo */}
      <mesh geometry={malhas.corpo} castShadow>
        <meshStandardMaterial color={ativa ? LARANJA_BRILHO : LARANJA_HOLMATRO} metalness={0.25} roughness={0.42} />
      </mesh>
      <mesh geometry={malhas.anelTraseiro}>
        <meshStandardMaterial color={GRAFITE_COLAR} metalness={0.5} roughness={0.35} />
      </mesh>
      <mesh geometry={malhas.tuboOleo}>
        <meshStandardMaterial color="#94a3b8" metalness={0.9} roughness={0.2} />
      </mesh>
      {detalhes && (
        <mesh position={[-0.26, 0.064, 0]}>
          <CaixaArredondada args={[0.02, 0.016, 0.018]} />
          <meshStandardMaterial color={GRAFITE_COLAR} roughness={0.4} />
        </mesh>
      )}

      {/* 3. Colar dianteiro */}
      <mesh geometry={malhas.colar} castShadow>
        <meshStandardMaterial color={GRAFITE_COLAR} metalness={0.45} roughness={0.35} />
      </mesh>

      {/* 4. Manopla de comando vertical, de pé sobre o colar */}
      <group position={[0, 0.06, 0]}>
        <mesh geometry={malhas.manopla} rotation={[0, 0, Math.PI / 2]} castShadow>
          <meshStandardMaterial color={BORRACHA_PUNHO} roughness={0.7} />
        </mesh>
        {detalhes && (
          <mesh position={[0, 0.205, 0]} rotation={[Math.PI / 2, 0, 0]}>
            <torusGeometry args={[0.016, 0.0025, 8, 16]} />
            <meshStandardMaterial color="#e2e8f0" metalness={0.95} roughness={0.15} />
          </mesh>
        )}
      </group>

      {/* 5. Alça de transporte paralela ao corpo */}
      <group position={[-0.01, 0.068, 0.048]}>
        {[0, -0.2].map((x) => (
          <mesh key={x} position={[x, 0.04, 0]}>
            <CaixaArredondada args={[0.024, 0.08, 0.02]} />
            <meshStandardMaterial color={GRAFITE_COLAR} roughness={0.4} />
          </mesh>
        ))}
        <mesh position={[-0.1, 0.075, 0]} rotation={[0, 0, Math.PI / 2]} castShadow>
          <cylinderGeometry args={[0.015, 0.015, 0.2, 16]} />
          <meshStandardMaterial color={BORRACHA_PUNHO} roughness={0.65} />
        </mesh>
      </group>

      {/* 6. Estágio 1: emerge do colar e estende por ext1 */}
      <mesh geometry={malhas.estagio} position={[0.04 + ext1 / 2, 0, 0]} scale={[ext1 + 0.01, 0.038, 0.038]}>
        <meshStandardMaterial color={ACO_CROMADO_ESTAGIO1} metalness={0.92} roughness={0.16} />
      </mesh>

      <group position={[0.04 + ext1, 0, 0]}>
        <mesh geometry={malhas.anelGuia1}>
          <meshStandardMaterial color="#1e293b" metalness={0.6} roughness={0.35} />
        </mesh>

        {/* Estágio 2: emerge de dentro do estágio 1 e estende por ext2 */}
        <mesh geometry={malhas.estagio} position={[ext2 / 2, 0, 0]} scale={[ext2 + 0.01, 0.027, 0.027]}>
          <meshStandardMaterial color={ACO_CROMADO_ESTAGIO2} metalness={0.95} roughness={0.12} />
        </mesh>
        <mesh geometry={malhas.anelGuia2} position={[ext2, 0, 0]}>
          <meshStandardMaterial color="#334155" metalness={0.7} roughness={0.3} />
        </mesh>
      </group>

      {/* 7. Cabeça de pressão na ponta do estágio 2 */}
      <group position={[posPonta, 0, 0]}>
        <GarraDentadaCruz malhas={malhas} detalhes={detalhes} />
      </group>
    </group>
  )
}
